import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, MessageCircle, Eye, Play } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

interface RecordingCardProps {
  recording: {
    id: string;
    title: string;
    description: string;
    video_url: string;
    thumbnail_url: string;
    view_count: number;
    duration: number;
    created_at: string;
    creator_id: string;
    profiles: {
      username: string;
      display_name: string;
      avatar_url: string;
      is_verified: boolean;
    };
    likes_count: number;
    comments_count: number;
    user_liked: boolean;
  };
  onUpdate?: () => void;
}

export function RecordingCard({ recording, onUpdate }: RecordingCardProps) {
  const { user } = useAuth();
  const [liked, setLiked] = useState(recording.user_liked);
  const [likesCount, setLikesCount] = useState(recording.likes_count);
  const [isLiking, setIsLiking] = useState(false);

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const formatViews = (count: number) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count.toString();
  };

  const handleLike = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please sign in to like recordings');
      return;
    }

    if (isLiking) return;

    try {
      setIsLiking(true);
      if (liked) {
        // Unlike
        const { error } = await supabase
          .from('recording_likes')
          .delete()
          .eq('recording_id', recording.id)
          .eq('user_id', user.id);

        if (error) throw error;
        setLiked(false);
        setLikesCount((prev) => prev - 1);
      } else {
        // Like
        const { error } = await supabase
          .from('recording_likes')
          .insert({ recording_id: recording.id, user_id: user.id });

        if (error) throw error;
        setLiked(true);
        setLikesCount((prev) => prev + 1);
      }
      onUpdate?.();
    } catch (error) {
      console.error('Error toggling like:', error);
      toast.error('Failed to update like');
    } finally {
      setIsLiking(false);
    }
  };

  return (
    <Link to={`/watch/${recording.id}`}>
      <Card className="group hover:shadow-lg transition-shadow overflow-hidden">
        <div className="relative aspect-video overflow-hidden bg-muted">
          <img
            src={recording.thumbnail_url || '/placeholder.svg'}
            alt={recording.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <Play className="w-16 h-16 text-white" />
          </div>
          <Badge className="absolute bottom-2 right-2 bg-black/80 text-white">
            {formatDuration(recording.duration)}
          </Badge>
        </div>

        <CardContent className="p-4">
          <div className="flex gap-3">
            <Link to={`/profile/${recording.creator_id}`} onClick={(e) => e.stopPropagation()}>
              <Avatar className="w-10 h-10">
                <AvatarImage src={recording.profiles.avatar_url} />
                <AvatarFallback>{recording.profiles.username[0]}</AvatarFallback>
              </Avatar>
            </Link>

            <div className="flex-1 min-w-0">
              <h3 className="font-semibold line-clamp-2 mb-1">
                {recording.title}
              </h3>
              <Link
                to={`/profile/${recording.creator_id}`}
                onClick={(e) => e.stopPropagation()}
                className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
              >
                {recording.profiles.display_name || recording.profiles.username}
                {recording.profiles.is_verified && (
                  <Badge variant="secondary" className="ml-1 h-4 px-1">
                    ✓
                  </Badge>
                )}
              </Link>

              <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Eye className="w-4 h-4" />
                  <span>{formatViews(recording.view_count)} views</span>
                </div>

                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto p-0 hover:bg-transparent"
                  onClick={handleLike}
                  disabled={isLiking}
                >
                  <div className="flex items-center gap-1">
                    <Heart
                      className={`w-4 h-4 ${liked ? 'fill-red-500 text-red-500' : ''}`}
                    />
                    <span>{likesCount}</span>
                  </div>
                </Button>

                <div className="flex items-center gap-1">
                  <MessageCircle className="w-4 h-4" />
                  <span>{recording.comments_count}</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
